/**
 * Where a customer goes to manage their side of a cloud connection.
 *
 * A cloud connection has two ends. We provision and monitor ours; the other
 * one lives in the customer's own account, and until now the detail view said
 * nothing about how to reach it — they had to go find it. Asked for by a
 * customer: *"un link directo para la consola de la nube… para gestionar su
 * enlace de su lado de AWS, GCP, OCI, etc."*
 *
 * Deliberately the generic sign-in root, not a deep link into their account.
 * Only AWS has a clean per-account console URL, and it needs an account id we
 * do not reliably hold — `detail.account` is the account on the link, which is
 * not guaranteed to be the one they sign into. The request was scoped that way
 * on purpose: *"el link es igual pa todos"*.
 *
 * The docs entry names the **product**, not the vendor, because that is what
 * makes the trip short: someone landing on "AWS documentation" still has to
 * find Direct Connect. Someone told the connection is a Direct Connect does
 * not.
 */

export interface CloudProviderLinks {
  /** How the cloud is written in prose — not the raw value from the payload. */
  displayName: string;
  /** The vendor's name for this kind of connection. */
  product: string;
  /** Generic sign-in root for the cloud's console. */
  console: string;
  /** The product's own documentation, not the vendor's doc home. */
  docs: string;
}

/**
 * Keyed by every spelling the payload is known to carry.
 *
 * `cloud` is a free `STRING` on `KontrolEdgeCloudConnectivity`, with no foreign
 * key to the `TypeClouds` table that holds the real vocabulary, so nothing
 * constrains what lands here. The seeded set is AWS · OCI · AZR · GCP with
 * long names beside them, and the backend's own fixtures carry `'aws'`,
 * `'AWS'` and `'  AWS  '` — hence the normalisation below and the aliases.
 */
const LINKS_BY_CLOUD: Readonly<Record<string, CloudProviderLinks>> = {
  aws: {
    displayName: "AWS",
    product: "AWS Direct Connect",
    console: "https://console.aws.amazon.com/",
    docs: "https://docs.aws.amazon.com/directconnect/latest/UserGuide/Welcome.html",
  },
  azure: {
    displayName: "Microsoft Azure",
    product: "Azure ExpressRoute",
    console: "https://portal.azure.com/",
    docs: "https://learn.microsoft.com/azure/expressroute/",
  },
  gcp: {
    displayName: "Google Cloud",
    product: "Cloud Interconnect",
    console: "https://console.cloud.google.com/",
    docs: "https://cloud.google.com/network-connectivity/docs/interconnect",
  },
  oci: {
    displayName: "Oracle Cloud",
    product: "OCI FastConnect",
    console: "https://cloud.oracle.com/",
    docs: "https://docs.oracle.com/en-us/iaas/Content/Network/Concepts/fastconnect.htm",
  },
};

/** Every spelling seen or seeded, mapped to a key of `LINKS_BY_CLOUD`. */
const ALIASES: Readonly<Record<string, keyof typeof LINKS_BY_CLOUD>> = {
  aws: "aws",
  amazon: "aws",
  "amazon web services": "aws",
  azr: "azure",
  azure: "azure",
  "microsoft azure": "azure",
  gcp: "gcp",
  google: "gcp",
  "google cloud": "gcp",
  "google cloud platform": "gcp",
  oci: "oci",
  oracle: "oci",
  "oracle cloud": "oci",
  "oracle cloud infrastructure": "oci",
};

/**
 * Resolves a `cloud` value to where its console and docs live, or `null` when
 * the value is absent or not one we recognise.
 *
 * `null` rather than a guess, and rather than a generic fallback: a link that
 * sends someone to the wrong vendor's console is worse than no link, and one
 * that says "cloud documentation" and lands on nothing in particular wastes
 * the click. The caller renders nothing. Same rule the status rows follow —
 * an absence of information is not evidence, and is not invented.
 */
export function resolveCloudProviderLinks(
  cloud: string | null | undefined
): CloudProviderLinks | null {
  const normalized = (cloud ?? "").toString().trim().toLowerCase();
  if (!normalized) return null;

  const key = ALIASES[normalized];

  return key ? LINKS_BY_CLOUD[key] : null;
}
